import { useState, useCallback } from 'react';
import { supabaseData } from '../../utils/supabase-data-client';
import { refetchAndCache } from './refetchAndCache';
import { useCacheStore } from './useCacheStore';
import { MutationResult } from './useCategoryMutations';

export type NewTemplateInput = {
  template_name: string;
  template_content?: string;
};

export type TemplateUpdateInput = {
  template_name?: string;
  template_content?: string;
};

interface UseNoteTemplateMutationsReturn {
  addTemplate: (input: NewTemplateInput) => Promise<MutationResult>;
  renameTemplate: (
    templateId: string,
    newName: string,
  ) => Promise<MutationResult>;
  updateTemplate: (
    templateId: string,
    updates: TemplateUpdateInput,
  ) => Promise<MutationResult>;
  deleteTemplate: (templateId: string) => Promise<MutationResult>;
  loading: boolean;
}

function validateName(name: string): { valid: boolean; trimmed: string } {
  const trimmed = name.trim();
  if (trimmed.length < 1 || trimmed.length > 100) {
    return { valid: false, trimmed };
  }
  return { valid: true, trimmed };
}

/**
 * CRUD for note templates.
 */
export const useNoteTemplateMutations = (): UseNoteTemplateMutationsReturn => {
  const [loading, setLoading] = useState(false);
  const cacheStore = useCacheStore();

  const addTemplate = useCallback(
    async (input: NewTemplateInput): Promise<MutationResult> => {
      const { valid, trimmed } = validateName(input.template_name);
      if (!valid) {
        return {
          success: false,
          error: 'Template name must be between 1 and 100 characters.',
        };
      }

      setLoading(true);
      try {
        const { error } = await supabaseData.from('note_templates').insert({
          template_name: trimmed,
          template_content: input.template_content ?? '',
        });

        if (error) {
          return { success: false, error: error.message };
        }

        await refetchAndCache('note_templates', cacheStore);
        return { success: true };
      } catch (err: unknown) {
        const message =
          err instanceof Error ? err.message : 'An unexpected error occurred.';
        return { success: false, error: message };
      } finally {
        setLoading(false);
      }
    },
    [cacheStore],
  );

  const updateTemplate = useCallback(
    async (
      templateId: string,
      updates: TemplateUpdateInput,
    ): Promise<MutationResult> => {
      const patch: Record<string, unknown> = {
        last_modified: new Date().toISOString(),
      };

      if (updates.template_name !== undefined) {
        const { valid, trimmed } = validateName(updates.template_name);
        if (!valid) {
          return {
            success: false,
            error: 'Template name must be between 1 and 100 characters.',
          };
        }
        patch.template_name = trimmed;
      }
      if (updates.template_content !== undefined) {
        patch.template_content = updates.template_content;
      }

      setLoading(true);
      try {
        const { error } = await supabaseData
          .from('note_templates')
          .update(patch)
          .eq('template_id', templateId);

        if (error) {
          return { success: false, error: error.message };
        }

        await refetchAndCache('note_templates', cacheStore);
        return { success: true };
      } catch (err: unknown) {
        const message =
          err instanceof Error ? err.message : 'An unexpected error occurred.';
        return { success: false, error: message };
      } finally {
        setLoading(false);
      }
    },
    [cacheStore],
  );

  const renameTemplate = useCallback(
    (templateId: string, newName: string): Promise<MutationResult> =>
      updateTemplate(templateId, { template_name: newName }),
    [updateTemplate],
  );

  const deleteTemplate = useCallback(
    async (templateId: string): Promise<MutationResult> => {
      setLoading(true);
      try {
        const { error } = await supabaseData
          .from('note_templates')
          .delete()
          .eq('template_id', templateId);

        if (error) {
          return { success: false, error: error.message };
        }

        await refetchAndCache('note_templates', cacheStore);
        return { success: true };
      } catch (err: unknown) {
        const message =
          err instanceof Error ? err.message : 'An unexpected error occurred.';
        return { success: false, error: message };
      } finally {
        setLoading(false);
      }
    },
    [cacheStore],
  );

  return {
    addTemplate,
    renameTemplate,
    updateTemplate,
    deleteTemplate,
    loading,
  };
};
